import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "../themeupdate/themeupdate.css";
import "./categories.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import useTour from "../../tour/TourContext";
import { splitAnswers, answersMatch } from "../../../functions/answerMatching";

const MAX_CATEGORIES = 6;
const MAX_YEARS_PER_CATEGORY = 4;
const MIN_QUESTIONS_PER_YEAR = 1;
const MAX_QUESTIONS_PER_YEAR = 5;
const MAX_NAME_LEN = 50;
const MAX_QUESTION_LEN = 150;
const MAX_OPTION_LEN = 50;
const MAX_ANSWER_LEN = 250;
const QUESTIONS_PAGE_SIZE = 10;

const QUESTION_TYPES = [
  { value: "radioselect", label: "Radio (Multiple Choice)" },
  { value: "typetext", label: "Text Input" },
  { value: "truefalse", label: "True / False" },
];

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const ensureArray = (val) => (Array.isArray(val) ? val : []);

const emptyCategory = () => ({
  id: genId(),
  name: "",
  color: "#667eea",
  icon: "",
  desktopBg: "",
  mobileBg: "",
  titleImage: "",
  years: [],
});

const VALID_IMAGE_EXT = ["jpg", "jpeg", "png"];
const validateImageFile = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (!VALID_IMAGE_EXT.includes(ext)) return "Only JPG, JPEG or PNG images are allowed";
  if (file.size > 5 * 1024 * 1024) return "Image must be less than 5MB";
  return null;
};

const validatePngFile = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext !== "png") return "Only PNG images are allowed";
  if (file.size > 5 * 1024 * 1024) return "Image must be less than 5MB";
  return null;
};

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image. Please try another file."));
    };
    img.src = url;
  });

const CATEGORY_ICON_SIZE = 500;
const validateCategoryIconFile = async (file) => {
  const err = validatePngFile(file);
  if (err) return err;
  const { width, height } = await getImageDimensions(file);
  if (width !== CATEGORY_ICON_SIZE || height !== CATEGORY_ICON_SIZE) {
    return `Image must be exactly ${CATEGORY_ICON_SIZE}×${CATEGORY_ICON_SIZE} pixels (yours is ${width}×${height}).`;
  }
  return null;
};

const emptyYear = () => ({ id: genId(), year: "", questions: [] });

const emptyQuestion = () => ({
  id: genId(),
  type: "radioselect",
  question: "",
  image: "",
  op1: "",
  op2: "",
  op3: "",
  answer: "",
  blurAnswer: false,
});

async function uploadImage({ file, token, sessionId }) {
  const fd = new FormData();
  fd.append("image", file);
  fd.append("sessionId", sessionId || "admin");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadCategoryImage`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Upload failed");
  return data.filename;
}

const s3Url = (filename) => (filename ? `${process.env.REACT_APP_S3_PATH || ""}${filename}` : "");

const Categories = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [questionSaving, setQuestionSaving] = useState(false);
  const [iconUploading, setIconUploading] = useState({});

  const [questionForm, setQuestionForm] = useState(null); // { categoryId, yearId, question, isNew, isGlobal }
  const [questionImageUploading, setQuestionImageUploading] = useState(false);

  // Page-level tabs: Categories → Years → Questions
  const [pageTab, setPageTab] = useState("categories"); // "categories" | "years" | "questions"
  const tour = useTour();

  // The guided tour drives which tab is open so it can highlight tab-specific content
  // (Years/Questions panels don't exist in the DOM until their tab is active).
  useEffect(() => {
    if (tour.active && tour.currentStep?.route === "/admin/categories" && tour.currentStep?.tab) {
      setPageTab(tour.currentStep.tab);
    }
  }, [tour.active, tour.currentStep]);
  const [yearsTabCategoryId, setYearsTabCategoryId] = useState(null);
  const [yearsSaving, setYearsSaving] = useState(false);
  const [yearInputValue, setYearInputValue] = useState("");
  const [questionsTabCategoryId, setQuestionsTabCategoryId] = useState("all");
  const [questionsPage, setQuestionsPage] = useState(1);

  // Background tabs: one per category + "Others"
  const [activeTabId, setActiveTabId] = useState(null); // categoryId | "others" | null
  const [bgModalCategoryId, setBgModalCategoryId] = useState(null);
  const [bgUploading, setBgUploading] = useState({}); // `${categoryId}-desktop` / `${categoryId}-mobile`
  const [othersModalOpen, setOthersModalOpen] = useState(false);
  const [yearIcon, setYearIcon] = useState("");
  const [questionBg, setQuestionBg] = useState("");
  const [othersUploading, setOthersUploading] = useState({}); // yearIcon / questionBg

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const cats = ensureArray(data.categories).map((c) => ({
      id: c.id || genId(),
      name: String(c.name ?? ""),
      color: c.color || "#667eea",
      icon: c.icon || "",
      desktopBg: c.desktopBg || "",
      mobileBg: c.mobileBg || "",
      titleImage: c.titleImage || "",
      years: ensureArray(c.years).map((y) => ({
        id: y.id || genId(),
        year: String(y.year ?? ""),
        questions: ensureArray(y.questions).map((q) => ({
          id: q.id || genId(),
          type: ["typetext", "truefalse"].includes(q.type) ? q.type : "radioselect",
          question: String(q.question ?? ""),
          image: q.image || "",
          op1: String(q.op1 ?? ""),
          op2: String(q.op2 ?? ""),
          op3: String(q.op3 ?? ""),
          answer: String(q.answer ?? ""),
          blurAnswer: Boolean(q.blurAnswer),
        })),
      })),
    }));
    setCategories(cats);
    setYearIcon(data.yearIcon || "");
    setQuestionBg(data.questionBg || "");
  }, [data]);

  const bgModalCategory = bgModalCategoryId ? categories.find((c) => c.id === bgModalCategoryId) : null;
  const activeYearsCategory = categories.find((c) => c.id === yearsTabCategoryId) || categories[0] || null;

  // ── Categories ──

  const addCategory = () => {
    if (categories.length >= MAX_CATEGORIES) {
      Swal.fire("Limit reached", `You can add up to ${MAX_CATEGORIES} categories.`, "warning");
      return;
    }
    setCategories((prev) => [...prev, emptyCategory()]);
  };

  const updateCategory = (id, patch) => {
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const removeCategory = async (id) => {
    const result = await Swal.fire({
      title: "Remove this category?",
      text: "All years and questions in this category will be deleted too.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove it",
    });
    if (!result.isConfirmed) return;
    setCategories((prev) => prev.filter((c) => c.id !== id));
    if (yearsTabCategoryId === id) setYearsTabCategoryId(null);
  };

  const handleIconUpload = async (categoryId, file) => {
    if (!file) return;
    setIconUploading((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const err = await validateCategoryIconFile(file);
      if (err) {
        Swal.fire("Error", err, "error");
        return;
      }
      const filename = await uploadImage({ file, token: adminToken, sessionId: organizationId || sessionId });
      updateCategory(categoryId, { icon: filename });
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setIconUploading((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  // ── Category background images (desktop/mobile), via tabs ──

  const handleCategoryBgUpload = async (categoryId, field, file, validator = validateImageFile) => {
    if (!file) return;
    const err = validator(file);
    if (err) {
      Swal.fire("Error", err, "error");
      return;
    }
    const key = `${categoryId}-${field}`;
    setBgUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const filename = await uploadImage({ file, token: adminToken, sessionId: organizationId || sessionId });
      updateCategory(categoryId, { [field]: filename });
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setBgUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openCategoryTab = (categoryId) => {
    setActiveTabId(categoryId);
    setBgModalCategoryId(categoryId);
  };

  // ── "Others" tab: year marker icon + question background ──

  const handleOthersUpload = async (field, file) => {
    if (!file) return;
    const err = validateImageFile(file);
    if (err) {
      Swal.fire("Error", err, "error");
      return;
    }
    setOthersUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const filename = await uploadImage({ file, token: adminToken, sessionId: organizationId || sessionId });
      if (field === "yearIcon") setYearIcon(filename);
      else setQuestionBg(filename);
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setOthersUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const openOthersTab = () => {
    setActiveTabId("others");
    setOthersModalOpen(true);
  };

  // ── Years ──

  const addYear = async (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) {
      Swal.fire("Pick a category", "Choose a category first.", "info");
      return null;
    }
    if (cat.years.length >= MAX_YEARS_PER_CATEGORY) {
      Swal.fire("Limit reached", `Up to ${MAX_YEARS_PER_CATEGORY} years per category.`, "warning");
      return null;
    }

    const existingYears = new Set(cat.years.map((y) => String(y.year).trim()));
    const { value: year, isConfirmed } = await Swal.fire({
      title: `Add a year — ${cat.name.trim() || "Untitled category"}`,
      input: "text",
      inputLabel: "Enter the year for this milestone (e.g. 1998)",
      inputPlaceholder: "1998",
      inputAttributes: { inputmode: "numeric", maxlength: 4 },
      showCancelButton: true,
      confirmButtonText: "Add",
      inputValidator: (val) => {
        const v = String(val || "").trim();
        if (!/^\d{3,4}$/.test(v)) return "Enter a valid year (e.g. 1998)";
        if (existingYears.has(v)) return "This category already has that year";
        return undefined;
      },
    });
    if (!isConfirmed) return null;

    const newYear = { ...emptyYear(), year: String(year).trim() };
    setCategories((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, years: [...c.years, newYear] } : c))
    );
    return newYear;
  };

  const removeYear = async (categoryId, yearId) => {
    const result = await Swal.fire({
      title: "Remove this year?",
      text: "All its questions will be deleted too.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove it",
    });
    if (!result.isConfirmed) return;
    setCategories((prev) =>
      prev.map((c) =>
        c.id !== categoryId ? c : { ...c, years: c.years.filter((y) => y.id !== yearId) }
      )
    );
  };

  // Inline tag-style "Add Year" used on the Years tab.
  const handleAddYearInline = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return;
    const v = yearInputValue.trim();
    if (!/^\d{3,4}$/.test(v)) {
      Swal.fire("Invalid year", "Enter a valid year, e.g. 1998.", "warning");
      return;
    }
    if (cat.years.length >= MAX_YEARS_PER_CATEGORY) {
      Swal.fire("Limit reached", `Up to ${MAX_YEARS_PER_CATEGORY} years per category.`, "warning");
      return;
    }
    if (cat.years.some((y) => String(y.year).trim() === v)) {
      Swal.fire("Duplicate", "This category already has that year.", "warning");
      return;
    }
    const newYear = { ...emptyYear(), year: v };
    setCategories((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, years: [...c.years, newYear] } : c))
    );
    setYearInputValue("");
  };

  // ── Questions ──

  // Opened from the Questions tab — category/year may be preset from the active filter tab.
  const startAddQuestionGlobal = (presetCategoryId = "") => {
    if (categories.length === 0) {
      Swal.fire("Add a category first", "Create a category before adding questions.", "info");
      return;
    }
    setQuestionForm({ categoryId: presetCategoryId, yearId: "", question: emptyQuestion(), isNew: true, isGlobal: true });
  };

  const startEditQuestion = (categoryId, yearId, question) => {
    setQuestionForm({ categoryId, yearId, question: { ...question }, isNew: false, isGlobal: false });
  };

  const handleQuestionFieldChange = (field, value) => {
    setQuestionForm((prev) => ({ ...prev, question: { ...prev.question, [field]: value } }));
  };

  const handleTypeChange = (newType) => {
    setQuestionForm((prev) => {
      const q = prev.question;
      if (newType === "truefalse") {
        return { ...prev, question: { ...q, type: newType, op1: "TRUE", op2: "FALSE", op3: "", answer: "" } };
      }
      if (newType === "typetext") {
        return { ...prev, question: { ...q, type: newType, op1: "", op2: "", op3: "" } };
      }
      // radioselect
      const wasTrueFalse = ["TRUE", "FALSE"].includes(q.op1) || ["TRUE", "FALSE"].includes(q.op2);
      return {
        ...prev,
        question: wasTrueFalse
          ? { ...q, type: newType, op1: "", op2: "", op3: "", answer: "" }
          : { ...q, type: newType },
      };
    });
  };

  const handleQuestionImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      Swal.fire("Error", "Only JPG, JPEG or PNG images are allowed", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Error", "Image must be less than 5MB", "error");
      e.target.value = "";
      return;
    }
    setQuestionImageUploading(true);
    try {
      const filename = await uploadImage({ file, token: adminToken, sessionId: organizationId || sessionId });
      handleQuestionFieldChange("image", filename);
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed", "error");
    } finally {
      setQuestionImageUploading(false);
      e.target.value = "";
    }
  };

  const validateQuestion = (q) => {
    if (!q.question.trim()) return "Question text is required";
    if (q.question.length > MAX_QUESTION_LEN) return `Question must be ${MAX_QUESTION_LEN} characters or less`;
    if (!q.answer.trim()) return "Answer is required";
    if (q.answer.length > MAX_ANSWER_LEN) return `Answer must be ${MAX_ANSWER_LEN} characters or less`;
    const acceptedAnswers = splitAnswers(q.answer);
    if (acceptedAnswers.length === 0) return "Answer is required";
    if (q.type === "radioselect") {
      if (!q.op1.trim() || !q.op2.trim() || !q.op3.trim()) return "All three options are required for radio questions";
      if ([q.op1, q.op2, q.op3].some((o) => o.length > MAX_OPTION_LEN)) return `Options must be ${MAX_OPTION_LEN} characters or less`;
      if (!acceptedAnswers.some((a) => [q.op1, q.op2, q.op3].some((o) => answersMatch(o, a)))) {
        return "At least one accepted answer must match one of the three options";
      }
    } else if (q.type === "truefalse") {
      const opts = [q.op1.trim().toUpperCase(), q.op2.trim().toUpperCase()];
      if (opts[0] !== "TRUE" && opts[0] !== "FALSE") return "Option 1 must be TRUE or FALSE";
      if (opts[1] !== "TRUE" && opts[1] !== "FALSE") return "Option 2 must be TRUE or FALSE";
      if (opts[0] === opts[1]) return "One option must be TRUE and the other FALSE";
      const ans = q.answer.trim().toUpperCase();
      if (ans !== "TRUE" && ans !== "FALSE") return "Answer must be TRUE or FALSE";
    }
    return null;
  };

  // Persists immediately (not just to local state) so an uploaded question image
  // reaches S3 + the DB as soon as this question is saved — previously this only
  // updated in-memory `categories` and relied on the admin remembering to click
  // the page-level Save button afterwards, which silently dropped the image
  // (and the whole question) if they navigated away first.
  const saveQuestionForm = async () => {
    if (!questionForm.categoryId) {
      Swal.fire("Validation", "Please select a category", "warning");
      return;
    }
    if (!questionForm.yearId) {
      Swal.fire("Validation", "Please select a year", "warning");
      return;
    }
    const err = validateQuestion(questionForm.question);
    if (err) {
      Swal.fire("Validation", err, "warning");
      return;
    }
    const targetYear = categories
      .find((c) => c.id === questionForm.categoryId)
      ?.years.find((y) => y.id === questionForm.yearId);
    if (!targetYear) {
      Swal.fire("Error", "That year no longer exists — pick another.", "error");
      return;
    }
    const alreadyInYear = targetYear.questions.some((q) => q.id === questionForm.question.id);
    if (questionForm.isNew && !alreadyInYear && targetYear.questions.length >= MAX_QUESTIONS_PER_YEAR) {
      Swal.fire("Limit reached", `Up to ${MAX_QUESTIONS_PER_YEAR} questions per year.`, "warning");
      return;
    }

    const updatedCategories = categories.map((c) => {
      if (c.id !== questionForm.categoryId) return c;
      return {
        ...c,
        years: c.years.map((y) => {
          if (y.id !== questionForm.yearId) return y;
          const exists = y.questions.some((q) => q.id === questionForm.question.id);
          const questions = exists
            ? y.questions.map((q) => (q.id === questionForm.question.id ? questionForm.question : q))
            : [...y.questions, questionForm.question];
          return { ...y, questions };
        }),
      };
    });

    setCategories(updatedCategories);
    setQuestionSaving(true);
    try {
      await persistThemeData(updatedCategories);
      setQuestionForm(null);
      Swal.fire({ icon: "success", title: "Question saved", timer: 1100, showConfirmButton: false });
    } catch (e) {
      Swal.fire(
        "Saved here, not on the server",
        `The question (and its image, if any) is kept on this page but failed to save to the server (${e.message || "unknown error"}). Click Save at the bottom to retry — don't leave this page until then.`,
        "warning"
      );
    } finally {
      setQuestionSaving(false);
    }
  };

  const deleteQuestion = async (categoryId, yearId, questionId) => {
    const result = await Swal.fire({
      title: "Delete this question?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });
    if (!result.isConfirmed) return;

    const updatedCategories = categories.map((c) =>
      c.id !== categoryId
        ? c
        : {
            ...c,
            years: c.years.map((y) =>
              y.id !== yearId ? y : { ...y, questions: y.questions.filter((q) => q.id !== questionId) }
            ),
          }
    );
    setCategories(updatedCategories);
    try {
      await persistThemeData(updatedCategories);
    } catch (e) {
      Swal.fire(
        "Deleted here, not on the server",
        `The question was removed on this page but failed to save to the server (${e.message || "unknown error"}). Click Save at the bottom to retry — don't leave this page until then.`,
        "warning"
      );
    }
  };

  // ── CSV import (client-side, whole category at once — see below) ──

  const TYPE_ALIASES = {
    text: "typetext",
    typetext: "typetext",
    "type text": "typetext",
    "text input": "typetext",
    "fill in the blank": "typetext",
    radio: "radioselect",
    radioselect: "radioselect",
    mcq: "radioselect",
    "multiple choice": "radioselect",
    truefalse: "truefalse",
    "true false": "truefalse",
    "true/false": "truefalse",
    tf: "truefalse",
  };

  const parseCsvRow = (line) => {
    const cells = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === "," && !inQuotes) { cells.push(cur); cur = ""; }
      else cur += ch;
    }
    cells.push(cur);
    return cells.map((c) => c.trim().replace(/^"|"$/g, "").trim());
  };

  // ── Whole-category bulk CSV import (one file, all years) ──

  const CSV_HEADERS = ["category", "year", "type", "question", "op1", "op2", "op3", "answer"];

  const downloadCategorySampleCsv = () => {
    if (categories.length === 0) {
      Swal.fire("Add a category first", "Create at least one category (Categories tab) so the sample can reference a real category name.", "info");
      return;
    }
    const cat1 = categories[0].name.trim() || "Category 1";
    const cat2 = (categories[1]?.name.trim() || categories[0].name.trim() || "Category 1");
    const sample = [
      [cat1, "1969", "radioselect", "India's National Space agency", "ISRO", "Space Commission", "Defense Space Agency", "ISRO"],
      [cat1, "1991", "typetext", "Architect of India's supercomputing initiative", "", "", "", "Vijay Bhatkar, Dr. Vijay Bhatkar"],
      [cat2, "1998", "truefalse", "ISRO launched India's first Mars mission in 2013", "TRUE", "FALSE", "", "TRUE"],
      [cat2, "1998", "typetext", "Where were India's 1998 nuclear tests conducted", "", "", "", "Pokhran, Pokharan"],
      [cat2, "2013", "typetext", "India's first spacecraft to Mars was called Mangalyaan", "", "", "", "Mangalyaan"],
      [cat1, "2002", "typetext", "Who is known as the Missile Man of India", "", "", "", "Abdul Kalam, A.P.J Abdul Kalam, Dr. Abdul Kalam, Dr. A.P.J Abdul Kalam, APJ Abdul Kalam"],
    ];
    const csv = [CSV_HEADERS.join(","), ...sample.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "questions_sample.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const csvCell = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

  const handleExportCsv = () => {
    const isAll = questionsTabCategoryId === "all";
    const cats = isAll ? categories : categories.filter((c) => c.id === questionsTabCategoryId);
    const rows = [];
    cats.forEach((c) => {
      c.years.forEach((y) => {
        y.questions.forEach((q) => {
          rows.push([c.name.trim() || "Untitled", y.year, q.type, q.question, q.op1, q.op2, q.op3, q.answer]);
        });
      });
    });

    if (rows.length === 0) {
      Swal.fire("Nothing to export", "No questions match this filter.", "info");
      return;
    }

    const csv = [CSV_HEADERS.map(csvCell).join(","), ...rows.map((r) => r.map(csvCell).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = isAll
      ? "all_questions_export.csv"
      : `${(cats[0]?.name.trim() || "category").replace(/\s+/g, "_")}_questions_export.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const findCategoryByName = (name) => {
    const n = String(name || "").trim().toLowerCase();
    if (!n) return null;
    return categories.find((c) => c.name.trim().toLowerCase() === n) || null;
  };

  // One file can carry rows for several categories at once — each row names its
  // own category via the "category" column (must match an existing category's
  // name exactly, case-insensitive). Categories themselves are never auto-created
  // here — only years within a matched category.
  const handleQuestionsCsvImport = async (file) => {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Only .csv files are supported (export your spreadsheet as CSV first, not .xlsx).", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("Error", "CSV file is too large (max 2MB).", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Replace ALL questions?",
      html: "This file becomes the sole source of truth for every category. Categories named in it get their <b>existing questions deleted and replaced</b> with what's in the file — and every category <b>not</b> named in it also gets its existing questions <b>deleted</b> (left empty).",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, replace them",
    });
    if (!confirm.isConfirmed) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = String(ev.target?.result || "");
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length < 2) throw new Error("CSV has no data rows");

        const skipped = [];
        let importedCount = 0;
        let yearsCreated = 0;
        const touchedCategoryIds = new Set();

        // Replace mode: the first row for a given category wipes its existing years/questions —
        // the CSV becomes the sole source of truth for every category it names. Categories never
        // mentioned in the file are left completely untouched (see the commit step below).
        const workingYears = new Map(); // categoryId -> years array (starts empty per touched category)
        const getWorkingYears = (cat) => {
          if (!workingYears.has(cat.id)) {
            workingYears.set(cat.id, []);
          }
          return workingYears.get(cat.id);
        };

        lines.slice(1).forEach((line, idx) => {
          const rowNum = idx + 2;
          const [rawCategory, rawYear, rawType, question, op1, op2, op3, answer] = parseCsvRow(line);

          const cat = findCategoryByName(rawCategory);
          if (!cat) {
            skipped.push(`Row ${rowNum}: unknown category "${rawCategory}" — create it on the Categories tab first`);
            return;
          }

          const yearKey = String(rawYear || "").trim();
          if (!/^\d{3,4}$/.test(yearKey)) {
            skipped.push(`Row ${rowNum}: invalid or missing year "${rawYear}"`);
            return;
          }

          const years = getWorkingYears(cat);
          let yearEntry = years.find((y) => String(y.year).trim() === yearKey);
          if (!yearEntry) {
            if (years.length >= MAX_YEARS_PER_CATEGORY) {
              skipped.push(`Row ${rowNum}: "${cat.name}" already has the max ${MAX_YEARS_PER_CATEGORY} years — year ${yearKey} skipped`);
              return;
            }
            yearEntry = { id: genId(), year: yearKey, questions: [] };
            years.push(yearEntry);
            yearsCreated++;
          }

          const normalizedType = TYPE_ALIASES[String(rawType || "").trim().toLowerCase()];
          if (!normalizedType) {
            skipped.push(`Row ${rowNum}: unknown type "${rawType}" (use "radio", "text" or "truefalse")`);
            return;
          }

          const isTF = normalizedType === "truefalse";
          const candidate = {
            id: genId(),
            type: normalizedType,
            question: (question || "").trim(),
            image: "",
            op1: isTF ? (op1 || "").trim().toUpperCase() : (op1 || "").trim(),
            op2: isTF ? (op2 || "").trim().toUpperCase() : (op2 || "").trim(),
            op3: (op3 || "").trim(),
            answer: isTF ? (answer || "").trim().toUpperCase() : (answer || "").trim(),
          };

          const err = validateQuestion(candidate);
          if (err) {
            skipped.push(`Row ${rowNum}: ${err}`);
            return;
          }

          // A later row can repeat an earlier row's question text within the same
          // category+year (both are new, from this same file — old questions are
          // already wiped for any touched category) — the later row wins and
          // replaces the earlier one in place, matching the "replace" semantics
          // of the import instead of silently dropping the later row.
          const dedupeKey = candidate.question.toLowerCase();
          const dupIndex = yearEntry.questions.findIndex(
            (q) => q.question.trim().toLowerCase() === dedupeKey
          );
          if (dupIndex === -1 && yearEntry.questions.length >= MAX_QUESTIONS_PER_YEAR) {
            skipped.push(`Row ${rowNum}: "${cat.name}" year ${yearKey} already has the max ${MAX_QUESTIONS_PER_YEAR} questions`);
            return;
          }

          if (dupIndex !== -1) {
            yearEntry.questions[dupIndex] = candidate;
          } else {
            yearEntry.questions.push(candidate);
          }
          importedCount++;
          touchedCategoryIds.add(cat.id);
        });

        // Nothing matched at all — every row was skipped (unknown category, bad type, failed
        // validation, etc). Nothing was touched or saved, so this must read as a failure, not
        // a misleading "Imported ... skipped" that sounds like something happened.
        if (importedCount === 0) {
          Swal.fire({
            icon: "error",
            title: "Nothing was imported",
            html: [
              "None of the rows matched an existing category (or all failed validation) — no questions were added or deleted.",
              "",
              "<b>Skipped:</b>",
              skipped.slice(0, 12).join("<br/>") + (skipped.length > 12 ? `<br/>…and ${skipped.length - 12} more` : ""),
            ].join("<br/>"),
          });
          return;
        }

        const touchedNames = categories
          .filter((c) => touchedCategoryIds.has(c.id))
          .map((c) => c.name.trim() || "Untitled")
          .join(", ");
        const clearedNames = categories
          .filter((c) => !touchedCategoryIds.has(c.id))
          .map((c) => c.name.trim() || "Untitled");
        const summaryLines = [
          `Imported ${importedCount} of ${lines.length - 1} rows into: <b>${touchedNames}</b>${yearsCreated ? ` (${yearsCreated} new year${yearsCreated === 1 ? "" : "s"})` : ""} — their previous questions were deleted and replaced with this file's rows.`,
        ];
        if (clearedNames.length) {
          summaryLines.push(`Not named in this file, so also cleared: <b>${clearedNames.join(", ")}</b>.`);
        }
        if (skipped.length) {
          summaryLines.push("", "<b>Skipped:</b>", skipped.slice(0, 12).join("<br/>") + (skipped.length > 12 ? `<br/>…and ${skipped.length - 12} more` : ""));
        }

        let saveFailed = false;
        // This file is now the sole source of truth for every category — one named in it gets
        // its questions replaced with the file's rows; one not named gets cleared entirely
        // (only a row that actually made it in "names" a category — one merely mentioned by a
        // row that failed validation is treated as not-named too, and gets cleared).
        const updatedCategories = categories.map((c) =>
          touchedCategoryIds.has(c.id) ? { ...c, years: workingYears.get(c.id) } : { ...c, years: [] }
        );
        setCategories(updatedCategories);
        try {
          await persistThemeData(updatedCategories);
          summaryLines.push("", "Saved to the server.");
        } catch (saveErr) {
          saveFailed = true;
          summaryLines.push(
            "",
            `<b>Warning:</b> imported here but failed to save to the server (${saveErr.message || "unknown error"}). Click Save at the bottom of the page to retry — don't refresh until then, or this import will be lost.`
          );
        }

        Swal.fire({
          icon: saveFailed ? "warning" : skipped.length ? "warning" : "success",
          title: saveFailed ? "Imported but not saved" : skipped.length ? "Imported with some rows skipped" : "Import successful",
          html: summaryLines.join("<br/>"),
        });
      } catch (err) {
        Swal.fire("Error", err.message || "Failed to parse CSV", "error");
      }
    };
    reader.readAsText(file);
  };

  // ── Save ──

  const serializeCategories = (list = categories) =>
    list.map((c) => ({
      id: c.id,
      name: c.name.trim(),
      color: c.color,
      icon: c.icon,
      desktopBg: c.desktopBg,
      mobileBg: c.mobileBg,
      titleImage: c.titleImage,
      years: c.years.map((y) => ({
        id: y.id,
        year: y.year,
        questions: y.questions.map((q) => ({
          id: q.id,
          type: q.type,
          question: q.question,
          image: q.image,
          op1: q.op1,
          op2: q.op2,
          op3: q.op3,
          answer: q.answer,
          blurAnswer: !!q.blurAnswer,
        })),
      })),
    }));

  // Flattens categories → years → questions into the row shape the SQL
  // `questions` table expects (one row per question, category/year as plain
  // columns rather than nested JSON).
  const flattenQuestionsForSqlTable = (list = categories) => {
    const rows = [];
    let sortOrder = 0;
    list.forEach((c) => {
      const categoryName = c.name.trim();
      c.years.forEach((y) => {
        y.questions.forEach((q) => {
          sortOrder += 1;
          let answerId = null;
          if (q.type === "radioselect") {
            const opts = [q.op1, q.op2, q.op3];
            const acceptedAnswers = splitAnswers(q.answer);
            const idx = opts.findIndex((o) => acceptedAnswers.some((a) => answersMatch(o, a)));
            answerId = idx >= 0 ? idx + 1 : null;
          } else if (q.type === "truefalse") {
            answerId = answersMatch(q.answer, q.op1) ? 1 : 2;
          } else {
            answerId = 1;
          }
          rows.push({
            sort_order: sortOrder,
            category: categoryName,
            question_name: q.question,
            option_one: q.op1 || "",
            option_two: q.op2 || "",
            option_three: q.op3 || "",
            image_question: q.image || "",
            correct_answer: q.answer,
            answer_id: answerId,
            year: y.year,
            type: q.type,
          });
        });
      });
    });
    return rows;
  };

  const syncCategoryQuestionsTable = async (list) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveCategoryQuestions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
      body: JSON.stringify({
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
        questions: flattenQuestionsForSqlTable(list),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to save questions table");
    }
  };

  const persistThemeData = (categoriesOverride) => {
    const list = categoriesOverride ?? categories;
    return Promise.all([
      updateThemeData({
        payload: {
          data: { yearIcon, questionBg, categories: serializeCategories(list) },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      }),
      syncCategoryQuestionsTable(list),
    ]).then(() => dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap());
  };

  const handleSave = async () => {
    if (categories.length === 0) {
      Swal.fire("Nothing to save", "Add at least one category first.", "info");
      return;
    }
    for (const c of categories) {
      if (!c.name.trim()) {
        Swal.fire("Validation", "Every category needs a name.", "warning");
        return;
      }
      if (c.name.length > MAX_NAME_LEN) {
        Swal.fire("Validation", `Category names must be ${MAX_NAME_LEN} characters or less.`, "warning");
        return;
      }
    }

    setSaving(true);
    try {
      await persistThemeData();
      Swal.fire("Success", "Categories saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save categories", "error");
    } finally {
      setSaving(false);
    }
  };

  // Quick save used by the background/others popups — persists whatever is
  // currently in state without the full name/year/question validation above,
  // since those popups only touch image fields.
  const [bgSaving, setBgSaving] = useState(false);
  const [othersSaving, setOthersSaving] = useState(false);

  const handleSaveBgModal = async () => {
    setBgSaving(true);
    try {
      await persistThemeData();
      Swal.fire("Success", "Background images saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setBgSaving(false);
    }
  };

  const handleSaveOthersModal = async () => {
    setOthersSaving(true);
    try {
      await persistThemeData();
      Swal.fire("Success", "Settings saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setOthersSaving(false);
    }
  };

  // Years tab's own quick save (no full validation — questions come next) —
  // saves then moves straight to the Questions tab.
  const handleSaveYearsContinue = async () => {
    if (categories.length === 0) {
      Swal.fire("Nothing to save", "Add at least one category first.", "info");
      return;
    }
    setYearsSaving(true);
    try {
      await persistThemeData();
      await Swal.fire({ icon: "success", title: "Years saved", timer: 1100, showConfirmButton: false });
      setPageTab("questions");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save years", "error");
    } finally {
      setYearsSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="categories-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back to Rules
        </button>
      </div>

      <div className="categories-panel">
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">
            <span>Categories &amp; Questions</span>
            <span className="categories-count-chip">{categories.length} / {MAX_CATEGORIES}</span>
          </div>

          <div className="categories-hint">
            Each category holds up to {MAX_YEARS_PER_CATEGORY} years, and each year needs {MIN_QUESTIONS_PER_YEAR}&ndash;{MAX_QUESTIONS_PER_YEAR} questions.
          </div>

          <div className="themeupdate-tabs categories-page-tabs">
            <button
              type="button"
              className={`themeupdate-tab ${pageTab === "categories" ? "active" : ""}`}
              onClick={() => setPageTab("categories")}
            >
              <i className="fa-solid fa-layer-group"></i> Categories
            </button>
            <button
              type="button"
              className={`themeupdate-tab ${pageTab === "years" ? "active" : ""}`}
              onClick={() => setPageTab("years")}
            >
              <i className="fa-regular fa-calendar"></i> Years
            </button>
            <button
              type="button"
              className={`themeupdate-tab ${pageTab === "questions" ? "active" : ""}`}
              onClick={() => setPageTab("questions")}
            >
              <i className="fa-solid fa-list-ul"></i> Questions
            </button>
          </div>

          {pageTab === "categories" && (
            <>
              <div className="themeupdate-tabs categories-bg-tabs">
                {categories.map((cat, i) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`themeupdate-tab ${activeTabId === cat.id ? "active" : ""}`}
                    onClick={() => openCategoryTab(cat.id)}
                  >
                    <i className="fa-solid fa-image"></i> {cat.name.trim() || `Category ${i + 1}`}
                  </button>
                ))}
                <button
                  type="button"
                  className={`themeupdate-tab ${activeTabId === "others" ? "active" : ""}`}
                  onClick={openOthersTab}
                >
                  <i className="fa-regular fa-calendar"></i> Others
                </button>
              </div>
              <p className="categories-bg-tabs-hint">
                Click a category to upload its desktop &amp; mobile background images. "Others" holds the year marker icon and question background image.
              </p>

              <div className="categories-grid">
                {categories.map((cat) => (
                  <div key={cat.id} className="category-card" style={{ "--cat-color": cat.color }}>
                    <button type="button" className="category-remove-btn" onClick={() => removeCategory(cat.id)} title="Remove category">
                      <i className="fa-solid fa-trash"></i>
                    </button>

                    <label className="category-icon-upload" title="PNG only, exactly 500×500px">
                      {cat.icon ? <img src={s3Url(cat.icon)} alt="" /> : <i className="fa-solid fa-image"></i>}
                      <span className="category-icon-overlay">
                        {iconUploading[cat.id] ? "Uploading…" : "Change"}
                      </span>
                      <input
                        type="file"
                        accept=".png"
                        onChange={(e) => handleIconUpload(cat.id, e.target.files?.[0])}
                      />
                    </label>

                    <input
                      type="text"
                      className="category-name-input"
                      placeholder="Category name"
                      maxLength={MAX_NAME_LEN}
                      value={cat.name}
                      onChange={(e) => updateCategory(cat.id, { name: e.target.value })}
                    />
                  </div>
                ))}

                {categories.length < MAX_CATEGORIES && (
                  <div className="categories-add-card" onClick={addCategory}>
                    <i className="fa-solid fa-plus"></i>
                    <span>Add Category</span>
                  </div>
                )}
              </div>
            </>
          )}

          {pageTab === "years" && (
            <div className="all-q-panel">
              {categories.length === 0 ? (
                <div className="themeupdate-empty">
                  <i className="fa-solid fa-inbox"></i>
                  <p>Add a category first (switch to the Categories tab).</p>
                </div>
              ) : (
                <>
                  <div className="themeupdate-tabs categories-bg-tabs">
                    {categories.map((cat, i) => (
                      <button
                        key={cat.id}
                        type="button"
                        className={`themeupdate-tab ${activeYearsCategory?.id === cat.id ? "active" : ""}`}
                        onClick={() => { setYearsTabCategoryId(cat.id); setYearInputValue(""); }}
                      >
                        {cat.name.trim() || `Category ${i + 1}`} ({cat.years.length}/{MAX_YEARS_PER_CATEGORY})
                      </button>
                    ))}
                  </div>

                  {activeYearsCategory && (
                    <div className="cy-year-body">
                      <h4 className="cy-tags-title">Select / Deselect Years</h4>
                      <div className="cy-tags-wrap">
                        {activeYearsCategory.years.length === 0 ? (
                          <span className="cy-tags-empty">No years yet</span>
                        ) : (
                          activeYearsCategory.years.map((yr) => {
                            const under = yr.questions.length < MIN_QUESTIONS_PER_YEAR;
                            return (
                              <span key={yr.id} className={`cy-tag ${under ? "cy-tag-warn" : ""}`}>
                                <span className="cy-tag-text">{yr.year || "?"}</span>
                                <button
                                  type="button"
                                  className="cy-tag-remove"
                                  onClick={() => removeYear(activeYearsCategory.id, yr.id)}
                                  title="Remove year"
                                >
                                  <i className="fa-solid fa-times"></i>
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>

                      <h4 className="cy-tags-title">Add a Year</h4>
                      <div className="cy-tag-add-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="cy-tag-input"
                          placeholder="New year e.g. 1998"
                          maxLength={4}
                          value={yearInputValue}
                          onChange={(e) => setYearInputValue(e.target.value.replace(/\D/g, ""))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddYearInline(activeYearsCategory.id);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-success"
                          onClick={() => handleAddYearInline(activeYearsCategory.id)}
                          disabled={activeYearsCategory.years.length >= MAX_YEARS_PER_CATEGORY}
                        >
                          <i className="fa-solid fa-plus"></i> Add Year
                        </button>
                      </div>
                      <p className="cy-tags-hint">
                        * Up to {MAX_YEARS_PER_CATEGORY} years per category ({activeYearsCategory.years.length}/{MAX_YEARS_PER_CATEGORY} used).<br />
                        * Years must be unique within a category.
                      </p>
                    </div>
                  )}

                </>
              )}
            </div>
          )}

          {pageTab === "questions" && (
            <div className="all-q-panel">
              <div className="cy-add-row">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={() => startAddQuestionGlobal(questionsTabCategoryId !== "all" ? questionsTabCategoryId : "")}
                >
                  <i className="fa-solid fa-plus"></i> Add Question
                </button>
                <label className="themeupdate-btn themeupdate-btn-primary" style={{ cursor: "pointer" }}>
                  <i className="fa-solid fa-upload"></i> Upload CSV
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleQuestionsCsvImport(file);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button type="button" className="themeupdate-btn themeupdate-btn-primary" onClick={handleExportCsv}>
                  <i className="fa-solid fa-download"></i> Export CSV
                </button>
                <button type="button" className="themeupdate-btn themeupdate-btn-outline" onClick={downloadCategorySampleCsv}>
                  <i className="fa-solid fa-file-csv"></i> Sample CSV
                </button>
              </div>

              {categories.length === 0 ? (
                <div className="themeupdate-empty">
                  <i className="fa-solid fa-inbox"></i>
                  <p>Add a category first (switch to the Categories tab).</p>
                </div>
              ) : (
                <>
                  <div className="themeupdate-tabs categories-bg-tabs">
                    <button
                      type="button"
                      className={`themeupdate-tab ${questionsTabCategoryId === "all" ? "active" : ""}`}
                      onClick={() => { setQuestionsTabCategoryId("all"); setQuestionsPage(1); }}
                    >
                      All
                    </button>
                    {categories.map((cat, i) => {
                      const count = cat.years.reduce((sum, y) => sum + y.questions.length, 0);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className={`themeupdate-tab ${questionsTabCategoryId === cat.id ? "active" : ""}`}
                          onClick={() => { setQuestionsTabCategoryId(cat.id); setQuestionsPage(1); }}
                        >
                          {cat.name.trim() || `Category ${i + 1}`} ({count})
                        </button>
                      );
                    })}
                  </div>

                  <p className="all-q-filter-hint">
                    <i className="fa-solid fa-circle-info"></i>
                    <span>
                      Each CSV row names its own category, so one upload can add questions to several categories at once.
                      The tabs below just filter what you see here &mdash; they don't change where an upload goes.
                      Category names must already exist (add them on the Categories tab first) or that row is skipped.
                      A successful upload replaces the questions for every category &mdash; ones named in the file get the file's rows, and any category left out of the file gets cleared too.
                      The "answer" column accepts multiple comma-separated values (wrap the cell in quotes) to cover name variants, titles, and alternate spellings &mdash; e.g. "Dr. Abdul Kalam, A.P.J Abdul Kalam". Matching ignores case.
                    </span>
                  </p>

                  {(() => {
                    const rows = categories
                      .filter((c) => questionsTabCategoryId === "all" || c.id === questionsTabCategoryId)
                      .flatMap((cat) => cat.years.flatMap((yr) => yr.questions.map((q) => ({ cat, yr, q }))));

                    if (rows.length === 0) {
                      return (
                        <div className="themeupdate-empty">
                          <i className="fa-solid fa-inbox"></i>
                          <p>No questions yet. Add one above, or upload a CSV for a specific category.</p>
                        </div>
                      );
                    }

                    const totalPages = Math.max(1, Math.ceil(rows.length / QUESTIONS_PAGE_SIZE));
                    const safePage = Math.min(questionsPage, totalPages);
                    if (safePage !== questionsPage) setQuestionsPage(safePage);
                    const start = (safePage - 1) * QUESTIONS_PAGE_SIZE;
                    const pagedRows = rows.slice(start, start + QUESTIONS_PAGE_SIZE);

                    return (
                      <>
                        <div className="themeupdate-table-wrapper">
                          <table className="themeupdate-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Category</th>
                                <th>Year</th>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedRows.map(({ cat, yr, q }, idx) => (
                                <tr key={q.id}>
                                  <td>{start + idx + 1}</td>
                                  <td>
                                    <span className="all-q-cat-badge" style={{ background: cat.color }}>
                                      {cat.name.trim() || "Untitled"}
                                    </span>
                                  </td>
                                  <td>{yr.year}</td>
                                  <td className="themeupdate-td-ellipsis">{q.question}</td>
                                  <td className="themeupdate-td-ellipsis">{q.answer}</td>
                                  <td>
                                    <div className="themeupdate-table-actions">
                                      <button type="button" className="themeupdate-btn-icon themeupdate-btn-icon-edit" onClick={() => startEditQuestion(cat.id, yr.id, q)}>
                                        <i className="fa-solid fa-edit"></i>
                                      </button>
                                      <button type="button" className="themeupdate-btn-icon themeupdate-btn-icon-delete" onClick={() => deleteQuestion(cat.id, yr.id, q.id)}>
                                        <i className="fa-solid fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {totalPages > 1 && (
                          <div className="all-q-pagination">
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-outline"
                              onClick={() => setQuestionsPage((p) => Math.max(1, p - 1))}
                              disabled={safePage === 1}
                            >
                              <i className="fa-solid fa-chevron-left"></i> Prev
                            </button>
                            <span className="all-q-pagination-info">
                              Page {safePage} of {totalPages} &middot; {rows.length} questions
                            </span>
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-outline"
                              onClick={() => setQuestionsPage((p) => Math.min(totalPages, p + 1))}
                              disabled={safePage === totalPages}
                            >
                              Next <i className="fa-solid fa-chevron-right"></i>
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {pageTab !== "questions" && (
            <div className="rules-action-holder categories-save-holder">
              {pageTab === "years" && (
                <button type="button" className="save-and-continue" onClick={handleSaveYearsContinue} disabled={yearsSaving}>
                  {yearsSaving ? "Saving..." : "Save & Continue to Questions"}
                </button>
              )}
              <button type="button" className="save-and-continue" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Add/Edit question modal ── */}
      {questionForm && (
        <div className="themeupdate-modal-overlay">
          <div className="themeupdate-modal">
            <div className="themeupdate-modal-header">
              <h3 className="themeupdate-modal-title">{questionForm.isNew ? "Add Question" : "Edit Question"}</h3>
              <button type="button" className="themeupdate-modal-close" onClick={() => setQuestionForm(null)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="themeupdate-modal-body">
              <div className="themeupdate-form-grid">
                {questionForm.isGlobal && (
                  <>
                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Category *</label>
                      <select
                        className="themeupdate-select"
                        value={questionForm.categoryId}
                        onChange={(e) => setQuestionForm((prev) => ({ ...prev, categoryId: e.target.value, yearId: "" }))}
                      >
                        <option value="">Select category</option>
                        {categories.map((c, i) => (
                          <option key={c.id} value={c.id}>{c.name.trim() || `Category ${i + 1}`}</option>
                        ))}
                      </select>
                    </div>
                    <div className="themeupdate-form-group">
                      <label className="themeupdate-label">Year *</label>
                      {(() => {
                        const selCat = categories.find((c) => c.id === questionForm.categoryId);
                        if (!selCat) {
                          return <p className="cy-add-hint" style={{ margin: 0 }}>Pick a category first</p>;
                        }
                        if (selCat.years.length === 0) {
                          return (
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-outline"
                              onClick={async () => {
                                const created = await addYear(selCat.id);
                                if (created) setQuestionForm((prev) => ({ ...prev, yearId: created.id }));
                              }}
                            >
                              <i className="fa-solid fa-plus"></i> Add a year first
                            </button>
                          );
                        }
                        return (
                          <select
                            className="themeupdate-select"
                            value={questionForm.yearId}
                            onChange={(e) => setQuestionForm((prev) => ({ ...prev, yearId: e.target.value }))}
                          >
                            <option value="">Select year</option>
                            {selCat.years.map((y) => (
                              <option key={y.id} value={y.id}>
                                {y.year || "?"} ({y.questions.length}/{MAX_QUESTIONS_PER_YEAR})
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </div>
                  </>
                )}

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Question Type</label>
                  <select
                    className="themeupdate-select"
                    value={questionForm.question.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">Question Text * (max {MAX_QUESTION_LEN} chars)</label>
                  <textarea
                    className="themeupdate-input themeupdate-textarea"
                    rows={3}
                    maxLength={MAX_QUESTION_LEN}
                    value={questionForm.question.question}
                    onChange={(e) => handleQuestionFieldChange("question", e.target.value)}
                  />
                </div>

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <div className="themeupdate-label-row">
                    <label className="themeupdate-label">Image (optional)</label>
                    <label className="themeupdate-toggle" title="Blurs the image on the player's screen until they submit an answer">
                      <input
                        type="checkbox"
                        checked={!!questionForm.question.blurAnswer}
                        onChange={(e) => handleQuestionFieldChange("blurAnswer", e.target.checked)}
                      />
                      <span className="themeupdate-toggle-track">
                        <span className="themeupdate-toggle-thumb" />
                      </span>
                      <span className="themeupdate-toggle-text">Blur image</span>
                    </label>
                  </div>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleQuestionImageChange}
                    className="themeupdate-file-input"
                    id="cq-image-input"
                  />
                  <label htmlFor="cq-image-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-image"></i> {questionImageUploading ? "Uploading..." : "Choose image"}
                  </label>
                  {questionForm.question.image && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(questionForm.question.image)} alt="Preview" />
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-outline themeupdate-image-remove-btn"
                        onClick={() => handleQuestionFieldChange("image", "")}
                      >
                        <i className="fa-solid fa-trash"></i> Remove image
                      </button>
                    </div>
                  )}
                </div>

                {questionForm.question.type === "radioselect" && (
                  <div className="themeupdate-form-group themeupdate-form-group-full">
                    <label className="themeupdate-label">Options * (one must match one of the accepted answers below)</label>
                    <div className="cq-options-grid">
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="Option 1"
                        maxLength={MAX_OPTION_LEN}
                        value={questionForm.question.op1}
                        onChange={(e) => handleQuestionFieldChange("op1", e.target.value)}
                      />
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="Option 2"
                        maxLength={MAX_OPTION_LEN}
                        value={questionForm.question.op2}
                        onChange={(e) => handleQuestionFieldChange("op2", e.target.value)}
                      />
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="Option 3"
                        maxLength={MAX_OPTION_LEN}
                        value={questionForm.question.op3}
                        onChange={(e) => handleQuestionFieldChange("op3", e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {questionForm.question.type === "truefalse" && (
                  <div className="themeupdate-form-group themeupdate-form-group-full">
                    <label className="themeupdate-label">Options (TRUE / FALSE)</label>
                    <div className="cq-options-grid">
                      <select
                        className="themeupdate-select"
                        value={questionForm.question.op1}
                        onChange={(e) => handleQuestionFieldChange("op1", e.target.value)}
                      >
                        <option value="TRUE">TRUE</option>
                        <option value="FALSE">FALSE</option>
                      </select>
                      <select
                        className="themeupdate-select"
                        value={questionForm.question.op2}
                        onChange={(e) => handleQuestionFieldChange("op2", e.target.value)}
                      >
                        <option value="FALSE">FALSE</option>
                        <option value="TRUE">TRUE</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">
                    {questionForm.question.type === "typetext" ? "Accepted Answer(s)" : "Answer"} * (max {MAX_ANSWER_LEN} chars)
                  </label>
                  {questionForm.question.type === "truefalse" ? (
                    <select
                      className="themeupdate-select"
                      value={questionForm.question.answer}
                      onChange={(e) => handleQuestionFieldChange("answer", e.target.value)}
                    >
                      <option value="">Select correct answer</option>
                      <option value="TRUE">TRUE</option>
                      <option value="FALSE">FALSE</option>
                    </select>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="themeupdate-input"
                        maxLength={MAX_ANSWER_LEN}
                        placeholder={
                          questionForm.question.type === "typetext"
                            ? "e.g. Dr. Abdul Kalam, A.P.J Abdul Kalam, Pokhran, Pokharan"
                            : ""
                        }
                        value={questionForm.question.answer}
                        onChange={(e) => handleQuestionFieldChange("answer", e.target.value)}
                      />
                      {questionForm.question.type === "typetext" && (
                        <div className="themeupdate-hint">
                          <i className="fa-solid fa-info-circle"></i> Separate multiple accepted answers with commas —
                          covers name variants, titles, and alternate spellings. Matching ignores case, so "abdul kalam"
                          and "ABDUL KALAM" are both fine.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="themeupdate-form-actions">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveQuestionForm}
                  disabled={questionSaving}
                >
                  {questionSaving ? "Saving..." : questionForm.isNew ? "Add Question" : "Update Question"}
                </button>
                <button type="button" className="themeupdate-btn themeupdate-btn-outline" onClick={() => setQuestionForm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Category background images modal ── */}
      {bgModalCategory && (
        <div className="themeupdate-modal-overlay">
          <div className="themeupdate-modal">
            <div className="themeupdate-modal-header">
              <h3 className="themeupdate-modal-title">{bgModalCategory.name || "Untitled category"} — Background Images</h3>
              <button type="button" className="themeupdate-modal-close" onClick={() => setBgModalCategoryId(null)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="themeupdate-modal-body">
              <div className="themeupdate-form-grid categories-bg-modal-grid">
                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Desktop Background</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="themeupdate-file-input"
                    id="cat-bg-desktop-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCategoryBgUpload(bgModalCategory.id, "desktopBg", file);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="cat-bg-desktop-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-desktop"></i>{" "}
                    {bgUploading[`${bgModalCategory.id}-desktopBg`] ? "Uploading..." : "Choose desktop image"}
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> Recommended 1920&times;1080, JPG/PNG, max 5MB
                  </div>
                  {bgModalCategory.desktopBg && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(bgModalCategory.desktopBg)} alt="Desktop background preview" />
                    </div>
                  )}
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Mobile Background</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="themeupdate-file-input"
                    id="cat-bg-mobile-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCategoryBgUpload(bgModalCategory.id, "mobileBg", file);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="cat-bg-mobile-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-mobile-screen"></i>{" "}
                    {bgUploading[`${bgModalCategory.id}-mobileBg`] ? "Uploading..." : "Choose mobile image"}
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> Recommended 414&times;896, JPG/PNG, max 5MB
                  </div>
                  {bgModalCategory.mobileBg && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(bgModalCategory.mobileBg)} alt="Mobile background preview" />
                    </div>
                  )}
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Main Head Title Image</label>
                  <input
                    type="file"
                    accept=".png"
                    className="themeupdate-file-input"
                    id="cat-bg-title-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCategoryBgUpload(bgModalCategory.id, "titleImage", file, validatePngFile);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="cat-bg-title-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-heading"></i>{" "}
                    {bgUploading[`${bgModalCategory.id}-titleImage`] ? "Uploading..." : "Choose title image"}
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> PNG only, max 5MB
                  </div>
                  {bgModalCategory.titleImage && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(bgModalCategory.titleImage)} alt="Title preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="themeupdate-form-actions categories-modal-actions-center">
                <button type="button" className="themeupdate-btn themeupdate-btn-success" onClick={handleSaveBgModal} disabled={bgSaving}>
                  {bgSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── "Others" modal: year icon + question background ── */}
      {othersModalOpen && (
        <div className="themeupdate-modal-overlay">
          <div className="themeupdate-modal">
            <div className="themeupdate-modal-header">
              <h3 className="themeupdate-modal-title">Other Settings</h3>
              <button type="button" className="themeupdate-modal-close" onClick={() => setOthersModalOpen(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="themeupdate-modal-body">
              <div className="themeupdate-form-grid">
                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Year Marker Icon</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="themeupdate-file-input"
                    id="others-year-icon-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleOthersUpload("yearIcon", file);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="others-year-icon-input" className="themeupdate-file-label-small">
                    <i className="fa-regular fa-calendar"></i>{" "}
                    {othersUploading.yearIcon ? "Uploading..." : "Choose icon"}
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> Used to mark every year point on the timeline. JPG/PNG, max 5MB
                  </div>
                  {yearIcon && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(yearIcon)} alt="Year icon preview" />
                    </div>
                  )}
                </div>

                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">Question Background Image</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="themeupdate-file-input"
                    id="others-question-bg-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleOthersUpload("questionBg", file);
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="others-question-bg-input" className="themeupdate-file-label-small">
                    <i className="fa-solid fa-image"></i>{" "}
                    {othersUploading.questionBg ? "Uploading..." : "Choose image"}
                  </label>
                  <div className="themeupdate-hint themeupdate-hint-image">
                    <i className="fa-solid fa-info-circle"></i> Shown behind the question popup during the game. JPG/PNG, max 5MB
                  </div>
                  {questionBg && (
                    <div className="themeupdate-image-preview">
                      <img src={s3Url(questionBg)} alt="Question background preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="themeupdate-form-actions categories-modal-actions-center">
                <button type="button" className="themeupdate-btn themeupdate-btn-success" onClick={handleSaveOthersModal} disabled={othersSaving}>
                  {othersSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
